/**
 * /table · ShareButton
 *
 * "Share this table" · founder doctrine 4 Jun 2026.
 *
 * Each verified venue can be shared via a deterministic URL built
 * from its `place_id` (or the venue.id when present). The link points
 * at /table/venue/{id} · the destination is the canonical detail
 * surface which re-fetches the venue from the verified pipeline ·
 * so the friend always sees the live verified data, never a stale
 * client-side snapshot.
 *
 * Two channels offered:
 *   1. WhatsApp deep-share (wa.me intent · honoured by mobile + web)
 *   2. Copy-to-clipboard (silent · always available · fallback for
 *      desktop where wa.me opens web.whatsapp.com)
 *
 * Honesty:
 *   - Copy reads "Verified table at {name} — they're booking-ready
 *     on /table → {url}" · NEVER "I booked you a table at …"
 *   - Both flows fire `/api/table/log-event` with user_action
 *     ∈ {share_whatsapp, share_copy_link} for downstream attribution.
 *
 * No new pipeline · the share URL hits the existing /table surface.
 */
import React, { useState } from "react";
import { logTableEvent } from "@/lib/table-api";

function buildShareUrl(venue) {
    const id = venue?.id;
    if (!id) return null;
    const origin = (typeof window !== "undefined" && window.location)
        ? `${window.location.protocol}//${window.location.host}`
        : "";
    // The detail-surface URL re-fetches verified data on open · the
    // friend always sees the current Places state, not our snapshot.
    return `${origin}/table?venue=${encodeURIComponent(id)}`;
}

function buildShareMessage(venue, url) {
    const name = (venue?.name || "this restaurant").trim();
    const city = venue?.city || "";
    const where = city ? ` (${city})` : "";
    return `Verified table at ${name}${where} — they're booking-ready on /table → ${url}`;
}

/**
 * `kind` accepts:
 *   "card"   — compact monospace pill that sits beside the map link
 *   "drawer" — slightly larger brass pill for the drawer header
 */
export function ShareButton({ venue, kind = "card" }) {
    const [copied, setCopied] = useState(false);
    const [open, setOpen] = useState(false);
    const url = buildShareUrl(venue);
    if (!url) return null;

    const message = buildShareMessage(venue, url);
    const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;

    const onWhatsApp = (e) => {
        e.stopPropagation();
        logTableEvent({
            resolver_output: { destinationType: "share_intent" },
            user_action: "share_whatsapp",
            venue: {
                name: venue?.name,
                evidence_source: venue?.evidence_source,
            },
            context: { share_url: url, share_kind: kind },
        });
        try {
            window.open(waUrl, "_blank", "noopener,noreferrer");
        } catch {
            // best-effort
        }
        setOpen(false);
    };

    const onCopy = async (e) => {
        e.stopPropagation();
        logTableEvent({
            resolver_output: { destinationType: "share_intent" },
            user_action: "share_copy_link",
            venue: {
                name: venue?.name,
                evidence_source: venue?.evidence_source,
            },
            context: { share_url: url, share_kind: kind },
        });
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(message);
            } else {
                // Legacy fallback for non-secure contexts
                const ta = document.createElement("textarea");
                ta.value = message;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            // last-ditch · open a new tab on the URL so the member
            // can still grab it from the address bar.
            window.open(url, "_blank", "noopener,noreferrer");
        }
        setOpen(false);
    };

    const isDrawer = kind === "drawer";
    const triggerStyle = isDrawer ? {
        fontSize: 11,
        letterSpacing: "0.14em",
        color: "var(--t-brass)",
        background: "var(--t-brass-wash)",
        border: "1px solid var(--t-brass2)",
        borderRadius: 30,
        padding: "5px 12px",
        fontWeight: 600,
        cursor: "pointer",
        whiteSpace: "nowrap",
    } : {
        fontSize: 9.5,
        letterSpacing: "0.14em",
        color: "var(--t-ink3)",
        background: "transparent",
        border: 0,
        cursor: "pointer",
        whiteSpace: "nowrap",
        fontWeight: 600,
        padding: 0,
    };

    return (
        <span
            style={{ position: "relative", display: "inline-block" }}
            onClick={(e) => e.stopPropagation()}
        >
            <button
                type="button"
                className="t-mono uppercase"
                data-testid={`share-button-${kind}`}
                aria-label="Share this table"
                aria-expanded={open}
                aria-haspopup="menu"
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((o) => !o);
                }}
                style={triggerStyle}
            >
                {isDrawer ? "share ↗" : "share ↗"}
            </button>

            {open ? (
                <span
                    role="menu"
                    data-testid={`share-menu-${kind}`}
                    style={{
                        position: "absolute",
                        top: "calc(100% + 6px)",
                        right: 0,
                        zIndex: 30,
                        background: "var(--t-paper2)",
                        border: "1px solid var(--t-line)",
                        borderRadius: 12,
                        boxShadow: "0 14px 32px rgba(33,28,21,0.18)",
                        padding: 6,
                        minWidth: 200,
                        display: "block",
                    }}
                >
                    <button
                        type="button"
                        role="menuitem"
                        data-testid="share-menu-whatsapp"
                        onClick={onWhatsApp}
                        style={menuItemStyle}
                    >
                        <span aria-hidden="true">●</span> WhatsApp
                    </button>
                    <button
                        type="button"
                        role="menuitem"
                        data-testid="share-menu-copy"
                        onClick={onCopy}
                        style={menuItemStyle}
                    >
                        <span aria-hidden="true">⧉</span> {copied ? "Copied ✓" : "Copy link"}
                    </button>
                </span>
            ) : null}
        </span>
    );
}

const menuItemStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    border: 0,
    background: "transparent",
    padding: "8px 10px",
    fontSize: 13,
    color: "var(--t-ink)",
    cursor: "pointer",
    borderRadius: 8,
    textAlign: "left",
    fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
};
